/**
 * Serviço de notificações push nativas do dispositivo.
 * Delega a exibição ao Service Worker via postMessage para maior
 * resiliência quando o app está em segundo plano (SO pode suspender o thread principal).
 */

export interface NotificationData {
  conversationId?: number;
  contactName?: string;
  url?: string;
}

const pushNotificationService = {
  /**
   * Verifica se o navegador suporta notificações
   */
  isSupported(): boolean {
    return typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator;
  },

  /**
   * Verifica se a permissão já foi concedida
   */
  isPermissionGranted(): boolean {
    if (!this.isSupported()) return false;
    return Notification.permission === "granted";
  },

  /**
   * Solicita permissão de notificação ao usuário
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) return false;

    try {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    } catch {
      return false;
    }
  },

  /**
   * Exibe uma notificação nativa no dispositivo via Service Worker (postMessage).
   * Fallback para registration.showNotification se o SW não estiver ativo.
   * Não depende mais de flag no localStorage — basta a permissão do navegador.
   */
  async showNotification(
    title: string,
    body: string,
    data?: NotificationData
  ): Promise<void> {
    if (!this.isPermissionGranted()) return;

    const tag = data?.conversationId ? `conversation-${data.conversationId}` : undefined;
    const payload = {
      type: "SHOW_NOTIFICATION" as const,
      title,
      body,
      icon: "/icon-192.png",
      badge: "/icon-192.png",
      tag,
      data,
      vibrate: [200, 100, 200],
      renotify: !!tag,
    };

    try {
      const registration = await navigator.serviceWorker.ready;

      if (registration.active) {
        // Delegar ao SW — mais resiliente a suspensão do thread principal
        registration.active.postMessage(payload);
      } else {
        // Fallback direto
        await registration.showNotification(title, {
          body,
          icon: "/icon-192.png",
          badge: "/icon-192.png",
          tag,
          data,
        } as NotificationOptions);
      }
    } catch (error) {
      console.error("[PushNotification] Failed to show notification:", error);
    }
  },

  /**
   * Habilita notificações (solicita permissão)
   */
  async enable(): Promise<boolean> {
    return this.requestPermission();
  },

  /**
   * Desabilita notificações (no-op — permissão é gerenciada pelo navegador)
   */
  disable(): void {
    // Permissão é gerenciada nativamente pelo navegador;
    // não há mais flag no localStorage.
  },
};

export default pushNotificationService;
